import { Resend } from 'resend';

const resendApiKey = import.meta.env.VITE_RESEND_API_KEY || '';

// Create Resend client with fallback empty string to prevent crashes
// The email functions will handle errors gracefully
const resend = new Resend(resendApiKey || 'placeholder-key');

const FROM_EMAIL = 'Verline <no-reply@verline.app>';
const ADMIN_EMAIL = import.meta.env.VITE_ADMIN_EMAIL || 'admin@verline.app';

// Export a function to check if Resend is properly configured
export const isResendConfigured = (): boolean => {
  return !!(resendApiKey && resendApiKey !== 'placeholder-key');
};

interface SendSignupConfirmationParams {
  email: string;
  name?: string;
  confirmationLink: string;
}

interface SendPasswordResetParams {
  email: string;
  resetLink: string;
}

interface SendAdminAlertParams {
  userEmail: string;
  userName?: string;
}

interface SendWelcomeEmailParams {
  email: string;
  name?: string;
}

interface SendNotificationEmailParams {
  to?: string | null;
  subject: string;
  body: string;
}

interface SendProjectInviteEmailParams {
  to: string;
  projectName: string;
  projectDescription: string;
}

export async function sendWelcomeEmail({
  email,
  name,
}: SendWelcomeEmailParams) {
  if (!isResendConfigured()) {
    console.warn('Resend API key is not configured. Welcome email will not be sent.');
    return { success: false, error: new Error('Resend API key is not configured') };
  }

  try {
    await resend.emails.send({
      from: FROM_EMAIL,
      to: email,
      subject: 'Welcome to Verline!',
      html: `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
          </head>
          <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #18181b; background-color: #ffffff; margin: 0; padding: 0;">
            <table role="presentation" style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 40px 20px; text-align: center; background-color: #000000;">
                  <div style="color: #ffffff; font-size: 24px; font-weight: 600; letter-spacing: -0.5px;">
                    Verline
                  </div>
                </td>
              </tr>
              <tr>
                <td style="padding: 40px 20px;">
                  <div style="max-width: 600px; margin: 0 auto;">
                    <h1 style="font-size: 28px; font-weight: 600; color: #000000; margin: 0 0 16px 0; letter-spacing: -0.5px;">
                      Welcome${name ? `, ${name}` : ''}!
                    </h1>
                    <p style="font-size: 16px; color: #52525b; margin: 0 0 24px 0;">
                      Thanks for signing up for Verline. We're excited to help you build trust with your receivers without the busy work.
                    </p>
                    <p style="font-size: 16px; color: #52525b; margin: 0 0 32px 0;">
                      Your account is ready to use. You can start creating projects and collaborating with your team right away.
                    </p>
                    <div style="text-align: center; margin: 32px 0;">
                      <a href="https://verline.app" 
                         style="display: inline-block; padding: 14px 32px; background-color: #000000; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 500; font-size: 15px;">
                        Go to Dashboard
                      </a>
                    </div>
                    <p style="font-size: 14px; color: #71717a; margin: 32px 0 0 0;">
                      If you have any questions, feel free to reach out to our support team.
                    </p>
                  </div>
                </td>
              </tr>
              <tr>
                <td style="padding: 24px 20px; text-align: center; background-color: #fafafa; border-top: 1px solid #e4e4e7;">
                  <p style="font-size: 12px; color: #71717a; margin: 0;">
                    © ${new Date().getFullYear()} Verline. All rights reserved.
                  </p>
                  <p style="font-size: 12px; color: #71717a; margin: 8px 0 0 0;">
                    <a href="https://verline.app" style="color: #52525b; text-decoration: none;">verline.app</a>
                  </p>
                </td>
              </tr>
            </table>
          </body>
        </html>
      `,
    });
    return { success: true };
  } catch (error) {
    console.error('Error sending welcome email:', error);
    return { success: false, error };
  }
}

export async function sendNotificationEmail({
  to,
  subject,
  body,
}: SendNotificationEmailParams) {
  if (!isResendConfigured() || !to) {
    console.warn('Notification email skipped: Resend not configured or missing recipient');
    return;
  }

  await resend.emails.send({
    from: FROM_EMAIL,
    to,
    subject,
    html: `<p style="font-family: sans-serif; color: #0f172a; line-height: 1.5;">${body}</p>`,
  });
}

export async function sendSignupConfirmation({
  email,
  name,
  confirmationLink,
}: SendSignupConfirmationParams) {
  try {
    await resend.emails.send({
      from: FROM_EMAIL,
      to: email,
      subject: 'Welcome to Verline - Confirm your email',
      html: `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
          </head>
          <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #18181b; background-color: #ffffff; margin: 0; padding: 0;">
            <table role="presentation" style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 40px 20px; text-align: center; background-color: #000000;">
                  <div style="color: #ffffff; font-size: 24px; font-weight: 600; letter-spacing: -0.5px;">
                    Verline
                  </div>
                </td>
              </tr>
              <tr>
                <td style="padding: 40px 20px;">
                  <div style="max-width: 600px; margin: 0 auto;">
                    <h1 style="font-size: 28px; font-weight: 600; color: #000000; margin: 0 0 16px 0; letter-spacing: -0.5px;">
                      Welcome${name ? `, ${name}` : ''}!
                    </h1>
                    <p style="font-size: 16px; color: #52525b; margin: 0 0 24px 0;">
                      Thanks for signing up for Verline. We're excited to help you build trust with your receivers without the busy work.
                    </p>
                    <p style="font-size: 16px; color: #52525b; margin: 0 0 32px 0;">
                      Please confirm your email address to get started:
                    </p>
                    <div style="text-align: center; margin: 32px 0;">
                      <a href="${confirmationLink}" 
                         style="display: inline-block; padding: 14px 32px; background-color: #000000; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 500; font-size: 15px;">
                        Confirm Email Address
                      </a>
                    </div>
                    <p style="font-size: 14px; color: #71717a; margin: 32px 0 0 0;">
                      Or copy and paste this link into your browser:<br>
                      <a href="${confirmationLink}" style="color: #52525b; word-break: break-all;">${confirmationLink}</a>
                    </p>
                    <p style="font-size: 14px; color: #71717a; margin: 24px 0 0 0;">
                      This link will expire in 24 hours. If you didn't create an account, you can safely ignore this email.
                    </p>
                  </div>
                </td>
              </tr>
              <tr>
                <td style="padding: 24px 20px; text-align: center; background-color: #fafafa; border-top: 1px solid #e4e4e7;">
                  <p style="font-size: 12px; color: #71717a; margin: 0;">
                    © ${new Date().getFullYear()} Verline. All rights reserved.
                  </p>
                  <p style="font-size: 12px; color: #71717a; margin: 8px 0 0 0;">
                    <a href="https://verline.app" style="color: #52525b; text-decoration: none;">verline.app</a>
                  </p>
                </td>
              </tr>
            </table>
          </body>
        </html>
      `,
    });
    return { success: true };
  } catch (error) {
    console.error('Error sending signup confirmation email:', error);
    return { success: false, error };
  }
}

export async function sendPasswordReset({
  email,
  resetLink,
}: SendPasswordResetParams) {
  try {
    await resend.emails.send({
      from: FROM_EMAIL,
      to: email,
      subject: 'Reset your Verline password',
      html: `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
          </head>
          <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #18181b; background-color: #ffffff; margin: 0; padding: 0;">
            <table role="presentation" style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 40px 20px; text-align: center; background-color: #000000;">
                  <div style="color: #ffffff; font-size: 24px; font-weight: 600; letter-spacing: -0.5px;">
                    Verline
                  </div>
                </td>
              </tr>
              <tr>
                <td style="padding: 40px 20px;">
                  <div style="max-width: 600px; margin: 0 auto;">
                    <h1 style="font-size: 28px; font-weight: 600; color: #000000; margin: 0 0 16px 0; letter-spacing: -0.5px;">
                      Reset Your Password
                    </h1>
                    <p style="font-size: 16px; color: #52525b; margin: 0 0 24px 0;">
                      You requested to reset your password for your Verline account. Click the button below to set a new password:
                    </p>
                    <div style="text-align: center; margin: 32px 0;">
                      <a href="${resetLink}" 
                         style="display: inline-block; padding: 14px 32px; background-color: #000000; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 500; font-size: 15px;">
                        Reset Password
                      </a>
                    </div>
                    <p style="font-size: 14px; color: #71717a; margin: 32px 0 0 0;">
                      Or copy and paste this link into your browser:<br>
                      <a href="${resetLink}" style="color: #52525b; word-break: break-all;">${resetLink}</a>
                    </p>
                    <p style="font-size: 14px; color: #71717a; margin: 24px 0 0 0;">
                      This link will expire in 1 hour. If you didn't request a password reset, you can safely ignore this email.
                    </p>
                  </div>
                </td>
              </tr>
              <tr>
                <td style="padding: 24px 20px; text-align: center; background-color: #fafafa; border-top: 1px solid #e4e4e7;">
                  <p style="font-size: 12px; color: #71717a; margin: 0;">
                    © ${new Date().getFullYear()} Verline. All rights reserved.
                  </p>
                  <p style="font-size: 12px; color: #71717a; margin: 8px 0 0 0;">
                    <a href="https://verline.app" style="color: #52525b; text-decoration: none;">verline.app</a>
                  </p>
                </td>
              </tr>
            </table>
          </body>
        </html>
      `,
    });
    return { success: true };
  } catch (error) {
    console.error('Error sending password reset email:', error);
    return { success: false, error };
  }
}

export async function sendAdminAlertOnSignup({
  userEmail,
  userName,
}: SendAdminAlertParams) {
  if (!isResendConfigured()) {
    console.warn('Resend API key is not configured. Admin alert email will not be sent.');
    return { success: false, error: new Error('Resend API key is not configured') };
  }

  try {
    await resend.emails.send({
      from: FROM_EMAIL,
      to: ADMIN_EMAIL,
      subject: `New user signup: ${userEmail}`,
      html: `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
          </head>
          <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #18181b; background-color: #ffffff; margin: 0; padding: 0;">
            <table role="presentation" style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 40px 20px;">
                  <div style="max-width: 600px; margin: 0 auto;">
                    <h1 style="font-size: 24px; font-weight: 600; color: #000000; margin: 0 0 16px 0;">
                      New User Signup
                    </h1>
                    <div style="background-color: #fafafa; border: 1px solid #e4e4e7; border-radius: 8px; padding: 20px; margin: 24px 0;">
                      <p style="font-size: 14px; color: #52525b; margin: 0 0 8px 0;">
                        <strong>Email:</strong> ${userEmail}
                      </p>
                      ${userName ? `
                        <p style="font-size: 14px; color: #52525b; margin: 0 0 8px 0;">
                          <strong>Name:</strong> ${userName}
                        </p>
                      ` : ''}
                      <p style="font-size: 14px; color: #52525b; margin: 8px 0 0 0;">
                        <strong>Signed up:</strong> ${new Date().toLocaleString()}
                      </p>
                    </div>
                    <p style="font-size: 14px; color: #71717a; margin: 24px 0 0 0;">
                      View user in Supabase dashboard or your admin panel.
                    </p>
                  </div>
                </td>
              </tr>
            </table>
          </body>
        </html>
      `,
    });
    return { success: true };
  } catch (error) {
    console.error('Error sending admin alert email:', error);
    return { success: false, error };
  }
}

/**
 * Send project invitation email to receiver
 * Email is sent from hello@verline.app with the exact project description
 */
export async function sendProjectInviteEmail({
  to,
  projectName,
  projectDescription,
}: SendProjectInviteEmailParams) {
  if (!isResendConfigured()) {
    console.warn('Resend API key is not configured. Project invitation email will not be sent.');
    throw new Error('Resend API key is not configured');
  }

  try {
    // Convert line breaks to HTML and preserve links
    // Simple function to convert newlines to <br> and detect URLs
    const formatDescription = (text: string): string => {
      if (!text) return '';

      // Convert newlines to <br>
      let formatted = text
        .replace(/\n/g, '<br>')
        .replace(/\r\n/g, '<br>');

      // Convert URLs to links (simple regex for http/https links)
      formatted = formatted.replace(
        /(https?:\/\/[^\s<]+)/g,
        '<a href="$1" style="color: #52525b; text-decoration: underline;">$1</a>'
      );

      return formatted;
    };

    const formattedDescription = formatDescription(projectDescription);

    await resend.emails.send({
      from: FROM_EMAIL, // Set to 'Verline <no-reply@verline.app>'
      to,
      subject: "You've been invited to a project on Verline",
      html: `
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
          </head>
          <body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; line-height: 1.6; color: #18181b; background-color: #ffffff; margin: 0; padding: 0;">
            <table role="presentation" style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="padding: 40px 20px;">
                  <div style="max-width: 600px; margin: 0 auto;">
                    <p style="font-size: 16px; color: #18181b; margin: 0 0 24px 0;">
                      Hi,
                    </p>
                    <p style="font-size: 16px; color: #18181b; margin: 0 0 24px 0;">
                      You've been invited to collaborate on a project on Verline.
                    </p>
                    ${projectDescription ? `
                      <div style="background-color: #fafafa; border-left: 3px solid #000000; padding: 16px 20px; margin: 24px 0;">
                        <p style="font-size: 14px; font-weight: 600; color: #000000; margin: 0 0 12px 0; text-transform: uppercase; letter-spacing: 0.5px;">
                          Message from the provider:
                        </p>
                        <div style="font-size: 16px; color: #18181b; line-height: 1.6;">
                          ${formattedDescription}
                        </div>
                      </div>
                    ` : ''}
                    <p style="font-size: 16px; color: #18181b; margin: 24px 0 0 0;">
                      If you have any questions, you can reply directly to this email.
                    </p>
                    <p style="font-size: 16px; color: #18181b; margin: 24px 0 0 0;">
                      — Verline
                    </p>
                  </div>
                </td>
              </tr>
            </table>
          </body>
        </html>
      `,
    });
    return { success: true };
  } catch (error) {
    console.error('Error sending project invitation email:', error);
    throw error;
  }
}

